import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { loadPayrollCalendar } from "./payrollCalendarLoader";
import { useEmployees } from "../../../hooks/useEmployees";
import { formatEmployeeLabel } from "../../../components/common/employeeUtils";
import { normalizeApiError, toDisplayString } from "../../../utils/normalizeApiError";
import "../../../styles/payroll-calendar.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function shiftMonth(monthKey, delta) {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function dateKey(iso) {
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : String(iso || "");
}

function dayNumber(iso) {
  const m = dateKey(iso).match(/^\d{4}-\d{2}-(\d{2})$/);
  return m ? parseInt(m[1], 10) : "";
}

function formatShortDate(iso) {
  const key = dateKey(iso);
  const m = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function statusLabel(status) {
  const map = {
    WORKING_DAY: "Working Day",
    WEEKLY_OFF: "Weekly Off",
    HOLIDAY: "Holiday",
    SPECIAL_WORKING_DAY: "Special Working Day",
  };
  return map[status] || status;
}

function statusPillClass(status) {
  if (status === "WORKING_DAY") return "pc-status-pill pc-status-pill--work";
  if (status === "WEEKLY_OFF") return "pc-status-pill pc-status-pill--off";
  if (status === "HOLIDAY") return "pc-status-pill pc-status-pill--hol";
  return "pc-status-pill pc-status-pill--spec";
}

function badgeClass(status) {
  if (status === "WORKING_DAY") return "pc-badge pc-badge--work";
  if (status === "WEEKLY_OFF") return "pc-badge pc-badge--off";
  if (status === "HOLIDAY") return "pc-badge pc-badge--hol";
  return "pc-badge pc-badge--spec";
}

function cellClass(day) {
  const parts = ["pc-cal-cell"];
  if (!day.in_month) parts.push("pc-cal-cell--muted");
  if (day.is_today) parts.push("pc-cal-cell--today");
  if (day.in_month) {
    if (day.calendar_status === "WORKING_DAY") parts.push("pc-cal-cell--working");
    if (day.calendar_status === "WEEKLY_OFF") parts.push("pc-cal-cell--off");
    if (day.calendar_status === "HOLIDAY") parts.push("pc-cal-cell--holiday");
    if (day.calendar_status === "SPECIAL_WORKING_DAY") parts.push("pc-cal-cell--special");
    if (day.half_day) parts.push("pc-cal-cell--half");
    if (day.ot) parts.push("pc-cal-cell--ot");
  }
  return parts.join(" ");
}

function payrollEventLabel(day) {
  if (day.salary_payment) return "Salary";
  if (day.payroll_processing) return "Payroll";
  return "—";
}

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function halfDayLabel(count) {
  if (!count) return "";
  return count === 1 ? "Half day" : `Half day ${count}`;
}

function otLabel(day) {
  if (!day?.ot) return "";
  const hours = formatHours(day.ot_hours);
  const amount = day.ot === 1 && hours ? `${hours}h` : hours ? `${day.ot} · ${hours}h` : String(day.ot);
  if (day.ot_status === "PENDING") return `OT ${amount} · Pending`;
  if (day.ot_status === "APPROVED") return `OT ${amount} · Approved`;
  if (day.ot_status === "MIXED") return `OT ${amount} · Mixed`;
  return `OT ${amount}`;
}

function otChipClass(status) {
  if (status === "PENDING") return "pc-mark pc-mark--ot-pending";
  if (status === "MIXED") return "pc-mark pc-mark--ot-mixed";
  return "pc-mark pc-mark--ot";
}

function employeeLocationId(emp) {
  const id = emp?.location?.location_id || emp?.location_id;
  return id ? String(id) : "";
}

function employeeDepartmentId(emp) {
  const id = emp?.department?.department_id || emp?.department_id;
  return id ? String(id) : "";
}

function uniqueNamedOptions(rows) {
  return [...rows.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

function DayCellContent({ day }) {
  const dayNum = dayNumber(day.date);
  const mutedMonth = !day.in_month
    ? MONTHS[parseInt(dateKey(day.date).slice(5, 7), 10) - 1]?.slice(0, 3).toUpperCase()
    : null;

  return (
    <>
      <div className="pc-cal-date-row">
        <div className="pc-cal-date">{dayNum}</div>
        {day.is_today && day.in_month ? <span className="pc-cal-today-tag">Today</span> : null}
        {mutedMonth ? <span className="pc-cal-muted-month">{mutedMonth}</span> : null}
      </div>
      {day.in_month ? (
        <>
          <div className={statusPillClass(day.calendar_status)}>
            <i aria-hidden />
            {statusLabel(day.calendar_status)}
          </div>
          {day.holiday_name ? (
            <div className="pc-cal-holiday-name" title={day.holiday_name}>
              {day.holiday_name}
            </div>
          ) : null}
          {(day.present != null || day.absent != null || day.leave != null) ? (
            <div className="pc-cal-att">
              {day.present != null ? <span>P {day.present}</span> : null}
              {day.absent != null ? <span>A {day.absent}</span> : null}
              {day.leave != null ? <span>L {day.leave}</span> : null}
            </div>
          ) : null}
          {day.half_day || day.ot ? (
            <div className="pc-cal-marks">
              {day.half_day ? <span className="pc-mark pc-mark--hd">{halfDayLabel(day.half_day)}</span> : null}
              {day.ot ? <span className={otChipClass(day.ot_status)}>{otLabel(day)}</span> : null}
            </div>
          ) : null}
          {day.payroll_processing ? <div className="pc-cal-marker">Payroll Processing</div> : null}
          {day.salary_payment ? <div className="pc-cal-marker pc-cal-marker--pay">Salary Payment</div> : null}
        </>
      ) : null}
    </>
  );
}

function DateDrawer({ day, onClose, payroll, employeeCount }) {
  if (!day) return null;
  return (
    <>
      <button type="button" className="pc-drawer-backdrop" aria-label="Close" onClick={onClose} />
      <aside className="pc-drawer" role="dialog" aria-labelledby="pc-drawer-title">
        <div className="pc-drawer-head">
          <h2 id="pc-drawer-title" style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800 }}>
            {formatShortDate(day.date)}
          </h2>
          <p style={{ margin: "0.25rem 0 0", color: "#64748b", fontSize: "0.875rem", fontWeight: 600 }}>
            {day.day_name}
          </p>
          <button type="button" className="pc-btn pc-btn--ghost" style={{ marginTop: "0.85rem" }} onClick={onClose}>
            Close
          </button>
        </div>
        <div className="pc-drawer-body">
          <div className="pc-drawer-section">
            <h4>Calendar status</h4>
            <div className="pc-drawer-row">
              <span>Status</span>
              <strong>{statusLabel(day.calendar_status)}</strong>
            </div>
            {day.shift?.shift_name ? (
              <>
                <div className="pc-drawer-row">
                  <span>Shift</span>
                  <strong>{day.shift.shift_name}</strong>
                </div>
                <div className="pc-drawer-row">
                  <span>Working hours</span>
                  <strong>
                    {[day.shift.expected_in, day.shift.expected_out].filter(Boolean).join(" – ") || "—"}
                  </strong>
                </div>
              </>
            ) : null}
            <div className="pc-drawer-row">
              <span>Weekly off</span>
              <strong>{day.is_weekly_off ? "Yes" : "No"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Holiday</span>
              <strong>{day.is_holiday ? day.holiday_name || "Yes" : "No"}</strong>
            </div>
          </div>
          <div className="pc-drawer-section">
            <h4>Attendance</h4>
            <div className="pc-drawer-row">
              <span>Present</span>
              <strong>{day.present ?? "—"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Absent</span>
              <strong>{day.absent ?? "—"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Leave</span>
              <strong>{day.leave ?? "—"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Half day</span>
              <strong>{day.half_day ? halfDayLabel(day.half_day) : "—"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>OT allocation</span>
              <strong>{day.ot ? otLabel(day) : "—"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Total employees</span>
              <strong>{employeeCount ?? "—"}</strong>
            </div>
          </div>
          <div className="pc-drawer-section">
            <h4>Payroll</h4>
            <div className="pc-drawer-row">
              <span>Payroll processing date</span>
              <strong>{day.payroll_processing ? "Yes" : "No"}</strong>
            </div>
            <div className="pc-drawer-row">
              <span>Salary payment date</span>
              <strong>{day.salary_payment ? "Yes" : "No"}</strong>
            </div>
            {payroll ? (
              <div className="pc-drawer-row">
                <span>Attendance cycle</span>
                <strong style={{ maxWidth: "55%", fontSize: "0.75rem" }}>{payroll.attendance_cycle_label}</strong>
              </div>
            ) : null}
          </div>
        </div>
      </aside>
    </>
  );
}

export default function PayrollCalendarPage() {
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [locationId, setLocationId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const { employees } = useEmployees();

  const [year, monthNum] = monthKey.split("-").map(Number);
  const monthLabel = `${MONTHS[monthNum - 1]} ${year}`;
  const yearOptions = useMemo(() => {
    const base = year || new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => base - 5 + i);
  }, [year]);

  const locations = useMemo(() => {
    const map = new Map();
    for (const emp of employees || []) {
      const id = employeeLocationId(emp);
      if (id) map.set(id, emp.location?.name || "Location");
    }
    return uniqueNamedOptions(map);
  }, [employees]);

  const departments = useMemo(() => {
    const map = new Map();
    for (const emp of employees || []) {
      if (locationId && employeeLocationId(emp) !== locationId) continue;
      const id = employeeDepartmentId(emp);
      if (id) map.set(id, emp.department?.name || "Department");
    }
    return uniqueNamedOptions(map);
  }, [employees, locationId]);

  const employeeOptions = useMemo(() => {
    return (employees || []).filter((emp) => {
      if (locationId && employeeLocationId(emp) !== locationId) return false;
      if (departmentId && employeeDepartmentId(emp) !== departmentId) return false;
      return true;
    });
  }, [employees, locationId, departmentId]);

  const onLocationChange = (value) => {
    setLocationId(value);
    setDepartmentId((current) => {
      if (!current || !value) return current;
      const stillThere = (employees || []).some(
        (emp) => employeeDepartmentId(emp) === current && employeeLocationId(emp) === value
      );
      return stillThere ? current : "";
    });
    setEmployeeId((current) => {
      if (!current) return "";
      const emp = (employees || []).find((row) => String(row.employee_id) === current);
      if (!emp || employeeLocationId(emp) !== value) return "";
      return current;
    });
  };

  const onDepartmentChange = (value) => {
    setDepartmentId(value);
    setEmployeeId((current) => {
      if (!current) return "";
      const emp = (employees || []).find((row) => String(row.employee_id) === current);
      if (!emp || employeeDepartmentId(emp) !== value) return "";
      return current;
    });
  };

  const onEmployeeChange = (value) => {
    setEmployeeId(value);
    if (!value) return;
    const emp = (employees || []).find((row) => String(row.employee_id) === value);
    if (!emp) return;
    setLocationId(employeeLocationId(emp));
    setDepartmentId(employeeDepartmentId(emp));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await loadPayrollCalendar({
        year,
        month: monthNum,
        location_id: locationId || undefined,
        department_id: departmentId || undefined,
        employee_id: employeeId || undefined,
        employee_ids: (locationId || departmentId || employeeId)
          ? (employees || [])
            .filter((emp) => {
              if (locationId && employeeLocationId(emp) !== locationId) return false;
              if (departmentId && employeeDepartmentId(emp) !== departmentId) return false;
              if (employeeId && String(emp.employee_id) !== employeeId) return false;
              return true;
            })
            .map((emp) => String(emp.employee_id))
          : undefined,
      });
      setData(res);
    } catch (e) {
      setData(null);
      setError(normalizeApiError(e));
    } finally {
      setLoading(false);
    }
  }, [year, monthNum, locationId, departmentId, employeeId, employees]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(() => {
    if (!selectedDay || !data) return null;
    const key = dateKey(selectedDay);
    return (
      (data.days || []).find((d) => dateKey(d.date) === key)
      || (data.grid_days || []).find((d) => dateKey(d.date) === key)
      || null
    );
  }, [selectedDay, data]);

  const summary = data?.summary;
  const payroll = data?.payroll;

  const openDay = (day) => {
    if (!day?.in_month) return;
    setSelectedDay(dateKey(day.date));
  };

  return (
    <div className="pc-page">
      <div className="pc-toolbar">
        <div className="pc-toolbar-left">
          <span className="pc-toolbar-title">{monthLabel}</span>
          <div className="pc-filters">
            <label className="pc-filter">
              <span className="pc-filter-label">Location</span>
              <select
                className="pc-select"
                value={locationId}
                onChange={(e) => onLocationChange(e.target.value)}
                aria-label="Location filter"
              >
                <option value="">All locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </label>
            <label className="pc-filter">
              <span className="pc-filter-label">Department</span>
              <select
                className="pc-select"
                value={departmentId}
                onChange={(e) => onDepartmentChange(e.target.value)}
                aria-label="Department filter"
              >
                <option value="">All departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </label>
            <label className="pc-filter">
              <span className="pc-filter-label">Employee</span>
              <select
                className="pc-select pc-select--employee"
                value={employeeId}
                onChange={(e) => onEmployeeChange(e.target.value)}
                aria-label="Employee filter"
              >
                <option value="">All employees</option>
                {employeeOptions.map((emp) => (
                  <option key={emp.employee_id} value={String(emp.employee_id)}>
                    {formatEmployeeLabel(emp)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {loading && data ? <span className="pc-loading-inline">Updating…</span> : null}
        </div>
        <div className="pc-toolbar-left">
          <button type="button" className="pc-btn" onClick={() => setMonthKey(currentMonthKey())}>
            Today
          </button>
          <button type="button" className="pc-btn" onClick={() => setMonthKey((m) => shiftMonth(m, -1))} aria-label="Previous month">
            <ChevronLeft size={16} />
          </button>
          <select
            className="pc-select"
            value={monthNum}
            onChange={(e) => setMonthKey(`${year}-${String(e.target.value).padStart(2, "0")}`)}
            aria-label="Month"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            className="pc-select"
            value={year}
            onChange={(e) => setMonthKey(`${e.target.value}-${String(monthNum).padStart(2, "0")}`)}
            aria-label="Year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button type="button" className="pc-btn" onClick={() => setMonthKey((m) => shiftMonth(m, 1))} aria-label="Next month">
            <ChevronRight size={16} />
          </button>
          <button type="button" className="pc-btn" onClick={load} disabled={loading} aria-label="Refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="pc-loading">Loading payroll calendar…</div>
      ) : null}

      {error ? (
        <div className="pc-error">
          {toDisplayString(error, "Unable to load payroll calendar.")}
          <div style={{ marginTop: "0.75rem" }}>
            <button type="button" className="pc-btn" onClick={load}>Retry</button>
          </div>
        </div>
      ) : null}

      {data && !error ? (
        <>
          {data._source === "legacy" ? (
            <div className="pc-info-banner" role="status">
              Calendar loaded from attendance and payroll settings. Restart the backend on port 9000 to enable the full payroll calendar API.
            </div>
          ) : null}
          <div className="pc-summary-grid">
            {[
              ["Calendar Days", summary?.calendar_days],
              ["Working Days", summary?.working_days],
              ["Weekly Offs", summary?.weekly_offs],
              ["Holidays", summary?.holidays],
              ["Present", summary?.present],
              ["Absent", summary?.absent],
              ["Leaves", summary?.leaves],
              ["Half day", summary?.half_days],
              ["OT", summary?.ot_allocations],
            ].map(([label, val]) => (
              <div key={label} className="pc-summary-card">
                <span>{label}</span>
                <strong>{val ?? "—"}</strong>
              </div>
            ))}
          </div>

          {payroll ? (
            <div className="pc-payroll-strip">
              <div>
                <strong>Attendance cycle</strong>
                <span>{payroll.attendance_cycle_label}</span>
              </div>
              <div>
                <strong>Payroll processing</strong>
                <span>{formatShortDate(payroll.payroll_processing_date)}</span>
              </div>
              <div>
                <strong>Salary payment</strong>
                <span>{formatShortDate(payroll.salary_payment_date)}</span>
              </div>
              <div>
                <strong>Working days divisor</strong>
                <span>{payroll.working_days_divisor ?? "—"}</span>
              </div>
              <div>
                <strong>LOP</strong>
                <span>{payroll.lop_enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <div>
                <strong>Attendance proration</strong>
                <span>{payroll.attendance_proration_enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
          ) : null}

          <div className="pc-legend" aria-label="Calendar legend">
            <span><i style={{ background: "#22c55e" }} /> Working Day</span>
            <span><i style={{ background: "#94a3b8" }} /> Weekly Off</span>
            <span><i style={{ background: "#f59e0b" }} /> Holiday</span>
            <span><i style={{ background: "#3b82f6" }} /> Special Working Day</span>
            <span><i style={{ background: "#7c3aed" }} /> OT allocation</span>
            <span><i style={{ background: "#2563eb" }} /> Half day</span>
            <span>▣ Payroll Processing</span>
            <span>₹ Salary Payment</span>
          </div>

          <div className="pc-cal-card">
            <div className="pc-cal-card-head">
              <h2>{monthLabel}</h2>
              <span>{summary?.employee_count ?? 0} employees · Click a date for details</span>
            </div>
            <div className="pc-cal-head">
              {WEEKDAY_HEADERS.map((h) => (
                <div key={h}>{h}</div>
              ))}
            </div>
            <div className="pc-cal-grid">
              {(data.grid_days || []).map((day) => (
                <button
                  key={dateKey(day.date)}
                  type="button"
                  className={cellClass(day)}
                  disabled={!day.in_month}
                  onClick={() => openDay(day)}
                  aria-label={`${formatShortDate(day.date)}, ${statusLabel(day.calendar_status)}`}
                >
                  <DayCellContent day={day} />
                </button>
              ))}
            </div>
            <div className="pc-mobile-list">
              {(data.days || []).map((day) => (
                <button
                  key={dateKey(day.date)}
                  type="button"
                  className="pc-mobile-day"
                  onClick={() => openDay(day)}
                >
                  <DayCellContent day={day} />
                </button>
              ))}
            </div>
          </div>

          <div className="pc-lower-grid">
            <div className="pc-box">
              <h3>{monthLabel} — Summary</h3>
              <dl>
                <div><dt>Total calendar days</dt><dd>{summary?.calendar_days}</dd></div>
                <div><dt>Total working days</dt><dd>{summary?.working_days}</dd></div>
                <div><dt>Total weekly offs</dt><dd>{summary?.weekly_offs}</dd></div>
                <div><dt>Total holidays</dt><dd>{summary?.holidays}</dd></div>
                <div><dt>Total present</dt><dd>{summary?.present}</dd></div>
                <div><dt>Total absent</dt><dd>{summary?.absent}</dd></div>
                <div><dt>Total leaves</dt><dd>{summary?.leaves}</dd></div>
                <div><dt>Half day</dt><dd>{summary?.half_days ?? 0}</dd></div>
                <div><dt>OT allocations</dt><dd>{summary?.ot_allocations ?? 0}{summary?.ot_hours ? ` · ${formatHours(summary.ot_hours)}h` : ""}</dd></div>
                <div><dt>Total employees</dt><dd>{summary?.employee_count}</dd></div>
              </dl>
            </div>

            <div>
              <h3 className="pc-section-title">Monthly Attendance &amp; Payroll Records</h3>
              <div className="pc-table-wrap">
                <table className="pc-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Status</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Leave</th>
                      <th>Half day</th>
                      <th>OT</th>
                      <th>Holiday</th>
                      <th>Work</th>
                      <th>Off</th>
                      <th>Payroll</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.records || []).map((row) => (
                      <tr
                        key={dateKey(row.date)}
                        onClick={() => setSelectedDay(dateKey(row.date))}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{formatShortDate(row.date)}</td>
                        <td>{row.day_name?.slice(0, 3)}</td>
                        <td><span className={badgeClass(row.calendar_status)}>{statusLabel(row.calendar_status)}</span></td>
                        <td>{row.present ?? "—"}</td>
                        <td>{row.absent ?? "—"}</td>
                        <td>{row.leave ?? "—"}</td>
                        <td>{row.half_day ? halfDayLabel(row.half_day) : "—"}</td>
                        <td>{row.ot ? otLabel(row) : "—"}</td>
                        <td>{row.holiday_name || "—"}</td>
                        <td>{row.is_working_day ? "✓" : "—"}</td>
                        <td>{row.is_weekly_off ? "✓" : "—"}</td>
                        <td>{payrollEventLabel(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                      <td colSpan={3}>TOTAL / SUMMARY</td>
                      <td>{summary?.present}</td>
                      <td>{summary?.absent}</td>
                      <td>{summary?.leaves}</td>
                      <td>{summary?.half_days ?? 0}</td>
                      <td>{summary?.ot_allocations ?? 0}</td>
                      <td colSpan={4}>
                        Days {summary?.calendar_days} · Work {summary?.working_days} · Off {summary?.weekly_offs} · Hol {summary?.holidays}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <DateDrawer
        day={selected}
        payroll={payroll}
        employeeCount={summary?.employee_count}
        onClose={() => setSelectedDay(null)}
      />
    </div>
  );
}
